import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Mail, Inbox, Loader2, Trash2, Eye,
  CheckCircle2, Clock, ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminEnquiriesPage = () => {
  const { api } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  useEffect(() => { loadEnquiries(); }, []);

  const loadEnquiries = async () => {
    try {
      const res = await api.get('/contact/enquiries');
      setEnquiries(res.data);
    } catch {
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/contact/enquiries/${id}`, { read: true, status: 'read' });
      setEnquiries(prev => prev.map(e => e.id === id ? { ...e, read: true, status: 'read' } : e));
    } catch {}
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    try {
      await api.delete(`/contact/enquiries/${id}`);
      setEnquiries(prev => prev.filter(e => e.id !== id));
      setSelectedEnquiry(null);
      toast.success('Enquiry deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openEnquiry = (enquiry) => {
    setSelectedEnquiry(enquiry);
    if (!enquiry.read) markRead(enquiry.id);
  };

  const unreadCount = enquiries.filter(e => !e.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-enquiries-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1" /> Admin</Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#2a3e5d]">Contact Enquiries</h1>
              <p className="text-sm text-slate-500">
                {enquiries.length} total{unreadCount > 0 && <> · <span className="text-[#20acf6] font-medium">{unreadCount} unread</span></>}
              </p>
            </div>
          </div>
        </div>

        {enquiries.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No enquiries yet. They'll appear here when visitors use the contact form.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {enquiries.map(e => (
              <div
                key={e.id}
                onClick={() => openEnquiry(e)}
                className={`p-4 rounded-lg border cursor-pointer transition-colors flex items-start gap-4 ${
                  e.read ? 'bg-white border-slate-200 hover:border-slate-300' : 'bg-[#20acf6]/5 border-[#20acf6]/20 hover:border-[#20acf6]/40'
                }`}
                data-testid={`enquiry-${e.id}`}
              >
                <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${e.read ? 'bg-slate-300' : 'bg-[#20acf6]'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-medium text-sm ${e.read ? 'text-slate-700' : 'text-[#2a3e5d] font-semibold'}`}>{e.name}</span>
                    <span className="text-xs text-slate-400">{e.email}</span>
                    {!e.read && <Badge className="bg-[#20acf6] text-white text-[10px] px-1.5">New</Badge>}
                  </div>
                  <p className="text-sm text-slate-600 font-medium">{e.subject}</p>
                  <p className="text-xs text-slate-400 truncate mt-0.5">{e.message}</p>
                </div>
                <div className="text-xs text-slate-400 shrink-0">
                  {new Date(e.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedEnquiry} onOpenChange={() => setSelectedEnquiry(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="enquiry-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-[#2a3e5d]">{selectedEnquiry?.subject}</DialogTitle>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-slate-700">{selectedEnquiry.name}</span>
                <a href={`mailto:${selectedEnquiry.email}`} className="text-[#20acf6] hover:underline">{selectedEnquiry.email}</a>
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {new Date(selectedEnquiry.created_at).toLocaleString('en-GB')}
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                {selectedEnquiry.message}
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7] flex-1">
                  <a href={`mailto:${selectedEnquiry.email}?subject=Re: ${encodeURIComponent(selectedEnquiry.subject)}`}>
                    <Mail className="h-4 w-4 mr-2" /> Reply via Email
                  </a>
                </Button>
                <Button variant="outline" size="icon" className="text-red-500 hover:text-red-700" onClick={() => deleteEnquiry(selectedEnquiry.id)} data-testid="delete-enquiry-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEnquiriesPage;
